import { Component, Directive, forwardRef, Input, Optional, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { NgbCalendar, NgbDate, NgbDateStruct, NgbInputDatepicker } from '@ng-bootstrap/ng-bootstrap';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, Validator } from '@angular/forms';
import { CapFormControlComponent } from '../form-control/form-control.component';
import { RegisterFormControlInputDirective } from '../form-control/register-form-control-input.directive';
import { TranslatePipe } from '@ngx-translate/core';

/*
 * Decorates NgbInputDatepicker to handle the popup and permit model bindings to ISO 8601 formatted strings
 * (which NgbInputDatepicker does not currently offer, cf.  https://github.com/ng-bootstrap/ng-bootstrap/issues/545 )
 * Use with ngModel or reactive forms.
 */
@Component({
  selector: 'cap-datepicker',
  host: {
    class: 'form-control-component'
  },
  template: `
    <input
      [footerTemplate]="enableFooter ? footerTemplate : null"
      [maxDate]="maxDate"
      [minDate]="minDate"
      capRegisterFormControlInput
      ngbDatepicker />
    <ng-template capRenderNextToDatePicker>
      @if (!datepicker.disabled) {
        <button (click)="togglePopup()" [attr.aria-label]="'Select date' | translate" class="btn btn-white" type="button">
          <i aria-hidden="true" class="far fa-calendar-alt"></i>
        </button>
      }
    </ng-template>
    <ng-template #footerTemplate>
      <hr class="my-0" />
      <button (click)="selectToday()" class="btn btn-primary btn-sm m-2 float-start" translate>{{ 'Today' | translate }}</button>
      <button (click)="close()" class="btn btn-secondary btn-sm m-2 float-end" translate>{{ 'Close' | translate }}</button>
    </ng-template>
  `,
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => DatepickerComponent), multi: true },
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => DatepickerComponent), multi: true }
  ],
  imports: [NgbInputDatepicker, RegisterFormControlInputDirective, forwardRef(() => CapRenderNextToDatePickerDirective), TranslatePipe]
})
export class DatepickerComponent implements ControlValueAccessor, Validator {
  @ViewChild(NgbInputDatepicker, { static: true })
  datepicker: NgbInputDatepicker;

  @Input()
  useDateStructs: boolean;

  @Input()
  minDate: NgbDateStruct = new NgbDate(1912, 1, 1);

  @Input()
  maxDate: NgbDateStruct;

  @Input()
  enableFooter = false;

  today = this.calendar.getToday();

  // We need this state for validation (because we don't write invalid dates to the model)
  // NgbInputDatePicker represents valid dates as NgbDateStruct, invalid dates as strings, and empty dates as null
  // We expose valid dates as iso string, and empty or invalid dates as null
  date: NgbDateStruct | string | null;

  constructor(
    @Optional() public formControl: CapFormControlComponent,
    public afterMeContainer: ViewContainerRef,
    private calendar: NgbCalendar
  ) {}

  togglePopup() {
    this.datepicker.toggle();
    if (this.formControl) {
      this.formControl.supressPopover = this.datepicker.isOpen();
    }
  }

  writeValue(obj): void {
    this.date = fromIso(this.useDateStructs, obj);
    this.datepicker.writeValue(this.date);
  }

  setDisabledState(isDisabled: boolean): void {
    return this.datepicker.setDisabledState(isDisabled);
  }

  registerOnChange(onChange): void {
    this.datepicker.registerOnChange((date) => {
      this.date = date;
      onChange(toIso(this.useDateStructs, this.date));
    });
  }

  registerOnTouched(fn): void {
    this.datepicker.registerOnTouched(fn);
  }

  validate(c: AbstractControl) {
    return this.datepicker.validate(<AbstractControl>{
      value: this.date
    });
  }

  selectToday() {
    this.date = this.today;
    this.datepicker.writeValue(this.date);
    this.close();
  }

  close() {
    this.datepicker.close();
  }
}

function fromIso(useDateStructs: boolean, iso: NgbDateStruct | string | null) {
  if (useDateStructs && iso && !iso['split']) {
    return iso;
  }
  if (iso) {
    const [year, month, day] = (iso as string).split('-').map(parse);
    return { year, month, day };
  } else {
    return null;
  }
}

function parse(s: string) {
  return parseInt(s, 10);
}

function toIso(useDateStructs: boolean, d: NgbDateStruct | string | null) {
  if (d === null || typeof d === 'string') {
    return null;
  }
  if (useDateStructs) {
    return d;
  }
  return [d.year, d.month, d.day].map(leftPad).join('-');
}

// no, we aren't going to import an npm package for this ;-)
function leftPad(n: number) {
  return n < 10 ? '0' + n : '' + n;
}

// as Bootstrap expects an input-group-append to be an immediate child of the input-group, we render it next to the host element
@Directive({
  selector: '[capRenderNextToDatePicker]'
})
export class CapRenderNextToDatePickerDirective {
  constructor(d: DatepickerComponent, templateRef: TemplateRef<void>) {
    d.afterMeContainer.createEmbeddedView(templateRef);
  }
}
